import { useEffect, useState } from 'react';

import { useAuth } from '@dotfile/frontend/shared/common';
import { usePrevious } from '@dotfile/frontend/shared/design-system';
import { useCaseChecksQueryLazyQuery } from '@dotfile/shared/data-access-client-portal';

import { ContactAuthContext } from '../../../../shared';
import {
  CASE_CHECK_INTERVAL_POLLING_IN_MS,
  useTryCompleteChecks,
} from '../../shared';

export const usePollCaseChecks = ({ skip }: { skip: boolean }) => {
  const {
    auth: { caseId },
  } = useAuth<ContactAuthContext>();
  const [tryCompleteChecks] = useTryCompleteChecks();

  const [isVisible, setIsVisible] = useState(true);
  const previousVisibility = usePrevious(isVisible);

  // Visibility change handler
  useEffect(() => {
    const onVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  const [fetchCaseChecksQuery, caseChecksQuery] = useCaseChecksQueryLazyQuery();

  useEffect(() => {
    // Wait for the case to load
    if (skip || !caseId) {
      return;
    }

    if (!isVisible) {
      return;
    }

    if (isVisible && !previousVisibility) {
      // Refetch when the tab is visible again
      fetchCaseChecksQuery({
        variables: { id: caseId },
        fetchPolicy: 'network-only',
      });
    }

    const intervalFn = async () => {
      // Poll the case to update checks status
      const res = await fetchCaseChecksQuery({
        variables: { id: caseId },
        fetchPolicy: 'network-only',
      });

      tryCompleteChecks(res.data?.case?.contactHasActions);
    };

    const interval = setInterval(intervalFn, CASE_CHECK_INTERVAL_POLLING_IN_MS);

    return () => clearInterval(interval);
  }, [
    skip,
    caseId,
    fetchCaseChecksQuery,
    isVisible,
    previousVisibility,
    tryCompleteChecks,
  ]);

  return {
    loading: caseChecksQuery.loading || !caseChecksQuery.called,
    error: caseChecksQuery.error,
    data: caseChecksQuery.data,
  };
};
